import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { syberKeyApi } from '../services/syberKeyApi';
import { checkCognitoSession as checkCognitoSessionSDK, signOutCognito } from '../services/cognitoService';

// API Service - use production URL by default, localhost for development
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (typeof window !== 'undefined' && 
   (window.location.hostname === 'sso.syberkey.com' || !window.location.hostname.includes('localhost'))
   ? 'https://api.syberkey.com' 
   : 'http://localhost:3000');

interface User {
  userId: string;
  email: string;
  fullName: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithSyberKey: () => Promise<void>;
  createSession: (userData: any) => Promise<void>;
  setToken: (token: string) => void;
  setUser: (user: User) => void;
  logout: () => Promise<void>;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on app load
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('auth_user');
    
    console.log('AuthContext useEffect - savedToken:', savedToken);
    console.log('AuthContext useEffect - savedUser:', savedUser);
    
    if (savedToken && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      console.log('AuthContext useEffect - parsedUser:', parsedUser);
      setToken(savedToken);
      setUser(parsedUser);
      setLoading(false);
    } else {
      // If no local session, check for Cognito session (for cross-subdomain SSO)
      // Cognito cookies are shared if domain is set to .maxlife-demo.syberkey.com
      const initializeCognitoSession = async () => {
        try {
          // First, try to get Cognito tokens from localStorage (same domain)
          const cognitoAccessToken = localStorage.getItem('cognito_access_token');
          const cognitoIdToken = localStorage.getItem('cognito_id_token');
          
          if (cognitoAccessToken && cognitoIdToken) {
            console.log('[AuthContext] Found Cognito tokens in localStorage, validating...');
            // Validate tokens with SyberKey service
            const validationResponse = await fetch(`${API_BASE_URL}/cognito-sso/validate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                id_token: cognitoIdToken,
                access_token: cognitoAccessToken,
                tenant_id: 'maxlife_26cc36'
              })
            });

            if (validationResponse.ok) {
              const validationData = await validationResponse.json();
              
              setToken(validationData.token);
              setUser(validationData.user);
              
              localStorage.setItem('auth_token', validationData.token);
              localStorage.setItem('auth_user', JSON.stringify(validationData.user));
              setLoading(false);
              return;
            }
          }
          
          // If no tokens in localStorage, check for Cognito session via SDK
          // This works across subdomains if Cognito cookies are shared
          console.log('[AuthContext] Checking for Cognito session via SDK (cross-subdomain)...');
          const cognitoSession = await checkCognitoSessionSDK();
          
          if (cognitoSession && cognitoSession.isValid) {
            console.log('[AuthContext] Valid Cognito session found via SDK, validating with backend...');
            
            // Store Cognito tokens in localStorage for this domain
            localStorage.setItem('cognito_access_token', cognitoSession.accessToken);
            localStorage.setItem('cognito_id_token', cognitoSession.idToken);
            if (cognitoSession.refreshToken) {
              localStorage.setItem('cognito_refresh_token', cognitoSession.refreshToken);
            }
            
            // Validate tokens with SyberKey service
            const validationResponse = await fetch(`${API_BASE_URL}/cognito-sso/validate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                id_token: cognitoSession.idToken,
                access_token: cognitoSession.accessToken,
                tenant_id: 'maxlife_26cc36'
              })
            });

            if (validationResponse.ok) {
              const validationData = await validationResponse.json();
              
              setToken(validationData.token);
              setUser(validationData.user);
              
              localStorage.setItem('auth_token', validationData.token);
              localStorage.setItem('auth_user', JSON.stringify(validationData.user));
              setLoading(false);
              return;
            }
          }
          
          console.log('[AuthContext] No valid Cognito session found');
          setLoading(false);
        } catch (error) {
          console.error('[AuthContext] Error checking Cognito session:', error);
          setLoading(false);
        }
      };
      
      initializeCognitoSession();
    }

    // Helper function to perform logout cleanup
    const performLogout = () => {
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('sso_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('cognito_access_token');
      localStorage.removeItem('cognito_id_token');
      localStorage.removeItem('cognito_refresh_token');
      localStorage.removeItem('sso_logout_flag');
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isLocalhost) {
        document.cookie = 'sso_token=; domain=.syberkey.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None; Secure';
      }
      window.location.href = '/login';
    };

    // Google-style: Periodic session validation to detect cross-app logout
    // Check session status every 10 seconds for faster detection
    const validationInterval = setInterval(async () => {
      // First, check for logout flag (works across different ports/apps)
      const logoutFlag = localStorage.getItem('sso_logout_flag');
      if (logoutFlag === 'true') {
        console.log('[AuthContext] Periodic check: Logout flag detected, verifying session...');
        // Verify session is actually invalidated before logging out
        const currentToken = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
        if (currentToken) {
          try {
            const validationResponse = await fetch(`${API_BASE_URL}/sso/validate-token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ token: currentToken })
            });

            if (!validationResponse.ok) {
              const errorData = await validationResponse.json().catch(() => ({}));
              if (errorData.error === 'session_invalidated' || errorData.crossAppLogout) {
                console.log('[AuthContext] Periodic check: Session confirmed invalidated - cross-app logout detected, logging out...');
                performLogout();
                return;
              }
            }
          } catch (error) {
            console.error('[AuthContext] Periodic logout flag verification failed:', error);
            // On error, assume logout is valid
            performLogout();
            return;
          }
        } else {
          // No token, logout is valid
          performLogout();
          return;
        }
      }

      // Also validate token periodically (even if no logout flag)
      const currentToken = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
      if (currentToken) {
        try {
          // Validate token with backend (checks session status)
          const validationResponse = await fetch(`${API_BASE_URL}/sso/validate-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token: currentToken })
          });

          if (!validationResponse.ok) {
            const errorData = await validationResponse.json().catch(() => ({}));
            // Check if session was invalidated (cross-app logout)
            if (errorData.error === 'session_invalidated' || errorData.crossAppLogout) {
              console.log('[AuthContext] Periodic check: Session invalidated - cross-app logout detected, logging out...');
              performLogout();
            }
          }
        } catch (error) {
          console.error('[AuthContext] Periodic session validation failed:', error);
        }
      }
    }, 10000); // Check every 10 seconds for faster detection

    // Listen for storage events (logout from other tabs/apps) - Google-style
    const handleStorageChange = (e: StorageEvent) => {
      // Check for logout flag
      if (e.key === 'sso_logout_flag' && e.newValue === 'true') {
        console.log('[AuthContext] Storage event: Logout flag detected from another tab/app');
        // Verify session is actually invalidated before logging out
        const currentToken = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
        if (currentToken) {
          fetch(`${API_BASE_URL}/sso/validate-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token: currentToken })
          }).then(async (response) => {
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              if (errorData.error === 'session_invalidated' || errorData.crossAppLogout) {
                console.log('[AuthContext] Storage event: Session confirmed invalidated, logging out...');
                setUser(null);
                setToken(null);
                localStorage.removeItem('auth_token');
                localStorage.removeItem('sso_token');
                localStorage.removeItem('auth_user');
                localStorage.removeItem('cognito_access_token');
                localStorage.removeItem('cognito_id_token');
                localStorage.removeItem('cognito_refresh_token');
                const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                if (!isLocalhost) {
                  document.cookie = 'sso_token=; domain=.syberkey.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None; Secure';
                }
                window.location.href = '/login';
              } else {
                // Session still valid, clear the flag
                localStorage.removeItem('sso_logout_flag');
              }
            }
          }).catch(() => {
            // On error, assume logout is valid
            setUser(null);
            setToken(null);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('sso_token');
            localStorage.removeItem('auth_user');
            window.location.href = '/login';
          });
        } else {
          // No token, logout is valid
          setUser(null);
          setToken(null);
          window.location.href = '/login';
        }
        return;
      }

      // Check for logout event broadcast
      if (e.key === 'sso_logout_event' && e.newValue) {
        console.log('[AuthContext] Storage event: Logout event detected from another tab/app');
        setUser(null);
        setToken(null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('sso_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('cognito_access_token');
        localStorage.removeItem('cognito_id_token');
        localStorage.removeItem('cognito_refresh_token');
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (!isLocalhost) {
          document.cookie = 'sso_token=; domain=.syberkey.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None; Secure';
        }
        window.location.href = '/login';
        return;
      }

      // Also check if token was removed
      if ((e.key === 'sso_token' || e.key === 'auth_token') && !e.newValue && e.oldValue) {
        console.log('[AuthContext] Storage event: Token removed from another tab/app');
        setUser(null);
        setToken(null);
        window.location.href = '/login';
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(validationInterval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [token]);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await syberKeyApi.loginUser(email, password);
      
      const userToken = response.token;
      const userData = response.user;
      
      setToken(userToken);
      setUser(userData);
      
      // Save to localStorage
      localStorage.setItem('auth_token', userToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithSyberKey = async () => {
    try {
      setLoading(true);
      
      // Check if user came from Cognito OIDC authentication
      const cognitoAccessToken = localStorage.getItem('cognito_access_token');
      const cognitoIdToken = localStorage.getItem('cognito_id_token');
      
      if (cognitoAccessToken && cognitoIdToken) {
        // Validate tokens with SyberKey service
        const validationResponse = await fetch('https://api.syberkey.com/cognito-sso/validate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id_token: cognitoIdToken,
            access_token: cognitoAccessToken,
            tenant_id: 'maxlife_26cc36'
          })
        });

        if (validationResponse.ok) {
          const validationData = await validationResponse.json();
          
          setToken(validationData.token);
          setUser(validationData.user);
          
          localStorage.setItem('auth_token', validationData.token);
          localStorage.setItem('auth_user', JSON.stringify(validationData.user));
          
          return;
        }
      }
      
      // Check if user came from SAML authentication (fallback)
      const urlParams = new URLSearchParams(window.location.search);
      const samlToken = urlParams.get('saml_token');
      const tenant = urlParams.get('tenant');
      
      if (samlToken && tenant) {
        // User authenticated via SAML, validate token
        const isValid = syberKeyApi.validateSSOToken(samlToken);
        if (isValid) {
          // For SAML users, we'll decode the token to get user info
          // In production, you'd validate this server-side
          setToken(samlToken);
          setUser({
            userId: `${tenant}-user`,
            email: `employee@${tenant}.com`,
            fullName: `${tenant.charAt(0).toUpperCase() + tenant.slice(1)} Employee`
          });
          
          localStorage.setItem('auth_token', samlToken);
          localStorage.setItem('auth_user', JSON.stringify({
            userId: `${tenant}-user`,
            email: `employee@${tenant}.com`, 
            fullName: `${tenant.charAt(0).toUpperCase() + tenant.slice(1)} Employee`
          }));
          
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
      }
      
      // No direct SSO redirect - user should use the LoginPage's lookup flow
      throw new Error('Please use the Login page for SSO authentication');
      
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createSession = async (userData: any) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        console.log('Creating session with user data:', userData);
        
        const userObject = {
          userId: userData.userId,
          email: userData.email,
          fullName: userData.fullName
        };

        // Create SSO token via backend API for cross-subdomain sharing
        try {
          console.log('[AuthContext] Calling /sso/create-token API...');
          console.log('[AuthContext] Request payload:', {
            userId: userData.userId,
            email: userData.email,
            tenantId: userData.tenantId || 'syberkey_sso_64a4c5',
            sessionId: userData.loginId
          });
          
          const ssoTokenResponse = await fetch(`${API_BASE_URL}/sso/create-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userId: userData.userId,
              email: userData.email,
              tenantId: userData.tenantId || 'syberkey_sso_64a4c5',
              sessionId: userData.loginId
            })
          });

          console.log('[AuthContext] SSO token API response status:', ssoTokenResponse.status);
          console.log('[AuthContext] SSO token API response ok:', ssoTokenResponse.ok);

          if (ssoTokenResponse.ok) {
            const ssoData = await ssoTokenResponse.json();
            const ssoToken = ssoData.token;
            
            console.log('[AuthContext] SSO token received from API');
            console.log('[AuthContext] SSO token length:', ssoToken ? ssoToken.length : 0);
            console.log('[AuthContext] SSO token preview (first 30 chars):', ssoToken ? ssoToken.substring(0, 30) : 'null');
            console.log('[AuthContext] SSO token has dots:', ssoToken ? ssoToken.includes('.') : false);
            console.log('[AuthContext] SSO token parts count:', ssoToken ? ssoToken.split('.').length : 0);

            if (!ssoToken || !ssoToken.includes('.')) {
              console.error('[AuthContext] ERROR: Received invalid JWT token from API - not a valid JWT format!');
              throw new Error('Invalid token format received from API');
            }

            // Set shared cookie for cross-subdomain SSO
            // For localhost, don't set domain (cookies work across ports on same hostname)
            // For production, set domain to .syberkey.com to share across subdomains
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const cookieExpires = new Date(Date.now() + 3600 * 1000).toUTCString(); // 1 hour
            const cookieDomain = isLocalhost ? '' : 'domain=.syberkey.com;';
            const cookieSecure = isLocalhost ? '' : 'Secure;';
            document.cookie = `sso_token=${ssoToken}; ${cookieDomain} path=/; expires=${cookieExpires}; SameSite=Lax; ${cookieSecure}`;

            // Also store in localStorage as fallback
            localStorage.setItem('sso_token', ssoToken);
            localStorage.setItem('auth_token', ssoToken);
            localStorage.setItem('auth_user', JSON.stringify(userObject));

            // Store additional session data
            if (userData.loginId) {
              localStorage.setItem('syberkey_login_id', userData.loginId);
            }
            if (userData.tenantId) {
              localStorage.setItem('syberkey_tenant_id', userData.tenantId);
            }

            setToken(ssoToken);
            setUser(userObject);

            console.log('[AuthContext] SSO session created successfully with shared cookie');
            console.log('[AuthContext] Current user state after creation:', userObject);
            
            resolve();
            return;
          } else {
            const errorText = await ssoTokenResponse.text();
            console.error('[AuthContext] SSO token API call failed with status:', ssoTokenResponse.status);
            console.error('[AuthContext] Error response:', errorText);
            throw new Error(`SSO token API failed: ${ssoTokenResponse.status} - ${errorText}`);
          }
        } catch (ssoError) {
          console.error('[AuthContext] Failed to create SSO token via API:', ssoError);
          const errorMessage = ssoError instanceof Error ? ssoError.message : String(ssoError);
          console.error('[AuthContext] Error details:', errorMessage);
          console.warn('[AuthContext] Falling back to local token (this will not work for SSO validation)');
          // Don't use fallback - throw error instead so user knows something is wrong
          reject(new Error(`Failed to create SSO token: ${errorMessage}`));
          return;
        }

        // Fallback: Create a local session token if API call fails
        const sessionToken = btoa(JSON.stringify({
          userId: userData.userId,
          email: userData.email,
          fullName: userData.fullName,
          loginId: userData.loginId,
          tenantId: userData.tenantId,
          exp: Date.now() + 24 * 60 * 60 * 1000 // 24 hours
        }));

        // Set shared cookie with fallback token
        const cookieExpires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `sso_token=${sessionToken}; domain=.syberkey.com; path=/; expires=${cookieExpires}; SameSite=None; Secure`;

        // Store in state and localStorage
        setToken(sessionToken);
        setUser(userObject);

        localStorage.setItem('auth_token', sessionToken);
        localStorage.setItem('sso_token', sessionToken);
        localStorage.setItem('auth_user', JSON.stringify(userObject));

        // Store additional session data
        if (userData.loginId) {
          localStorage.setItem('syberkey_login_id', userData.loginId);
        }
        if (userData.tenantId) {
          localStorage.setItem('syberkey_tenant_id', userData.tenantId);
        }

        console.log('Session created successfully (fallback mode)');
        console.log('Current user state after creation:', userObject);
        
        resolve();
      } catch (error) {
        console.error('Error creating session:', error);
        reject(error);
      }
    });
  };

  const logout = async () => {
    try {
      console.log('[AuthContext] Logout initiated');
      
      // Get SSO token for backend logout
      const ssoToken = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
      
      // Set logout flag to notify other tabs/apps BEFORE clearing tokens
      localStorage.setItem('sso_logout_flag', 'true');
      console.log('[AuthContext] Logout flag set to notify other apps');
      
      // Google-style: Call backend logout endpoint to invalidate ALL sessions
      // This ensures cross-application logout works properly
      if (ssoToken) {
        try {
          console.log('[AuthContext] Calling backend logout API to invalidate all sessions (Google-style)...');
          const logoutResponse = await fetch(`${API_BASE_URL}/sso/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              token: ssoToken,
              allSessions: true // Always true for Google-style cross-app logout
            })
          });
          
          if (logoutResponse.ok) {
            const result = await logoutResponse.json();
            console.log('[AuthContext] Backend logout API call successful');
            console.log('[AuthContext] Sessions invalidated:', result.sessionsInvalidated);
            console.log('[AuthContext] Cross-app logout:', result.crossAppLogout);
          } else {
            const errorText = await logoutResponse.text();
            console.warn('[AuthContext] Backend logout API returned error:', logoutResponse.status, errorText);
          }
        } catch (logoutError) {
          console.warn('[AuthContext] Backend logout failed, continuing with local logout:', logoutError);
        }
      }
      
      // Broadcast logout event to other tabs/windows
      try {
        window.localStorage.setItem('sso_logout_event', Date.now().toString());
        window.localStorage.removeItem('sso_logout_event');
        console.log('[AuthContext] Logout event broadcasted to other tabs');
      } catch (e) {
        console.warn('[AuthContext] Failed to broadcast logout event:', e);
      }
      
      // Get Cognito logout URL if tokens exist
      const cognitoAccessToken = localStorage.getItem('cognito_access_token');
      const cognitoIdToken = localStorage.getItem('cognito_id_token');
      
      if (cognitoAccessToken && cognitoIdToken) {
        console.log('[AuthContext] Attempting Cognito logout');
        
        try {
          // Determine redirect URI after logout
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const redirectUri = isLocalhost 
            ? `${window.location.protocol}//${window.location.host}/`
            : `https://sso.syberkey.com/`;
          
          // Get Cognito logout URL from backend
          const logoutResponse = await fetch(`${API_BASE_URL}/cognito-sso/logout?redirect_uri=${encodeURIComponent(redirectUri)}`, {
            method: 'GET'
          });

          if (logoutResponse.ok) {
            const logoutData = await logoutResponse.json();
            console.log('[AuthContext] Cognito logout URL received:', logoutData.logout_url);
            
            // Clear SSO cookie (shared across subdomains)
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            if (!isLocalhost) {
              document.cookie = 'sso_token=; domain=.syberkey.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None; Secure';
            }
            
            // Clear all local storage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('sso_token');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('cognito_access_token');
            localStorage.removeItem('cognito_id_token');
            localStorage.removeItem('cognito_refresh_token');
            localStorage.removeItem('cognito_sso_state');
            localStorage.removeItem('cognito_user_email');
            localStorage.removeItem('cognito_user_name');
            localStorage.removeItem('cognito_user_id');
            localStorage.removeItem('syberkey_login_id');
            localStorage.removeItem('syberkey_tenant_id');
            
            // Keep logout flag for a moment so other tabs can detect it
            setTimeout(() => {
              localStorage.removeItem('sso_logout_flag');
            }, 1000);
            
            // Clear state
            setUser(null);
            setToken(null);
            
            // Sign out from Cognito SDK (clears cookies shared across subdomains)
            signOutCognito();
            
            // Redirect to Cognito logout URL (will redirect back to redirectUri after logout)
            window.location.href = logoutData.logout_url;
            return;
          } else {
            console.warn('[AuthContext] Failed to get Cognito logout URL:', logoutResponse.status);
          }
        } catch (cognitoError) {
          console.warn('[AuthContext] Cognito logout failed, falling back to local logout:', cognitoError);
        }
      }

      // Fallback: local logout
      console.log('[AuthContext] Performing local logout');
      
      // Clear SSO cookie (shared across subdomains)
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isLocalhost) {
        document.cookie = 'sso_token=; domain=.syberkey.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None; Secure';
      }
      
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('sso_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('cognito_access_token');
      localStorage.removeItem('cognito_id_token');
      localStorage.removeItem('cognito_refresh_token');
      localStorage.removeItem('cognito_sso_state');
      localStorage.removeItem('syberkey_login_id');
      localStorage.removeItem('syberkey_tenant_id');
      // Keep logout flag for a moment so other tabs can detect it
      setTimeout(() => {
        localStorage.removeItem('sso_logout_flag');
      }, 1000);
      
      // Sign out from Cognito SDK (clears cookies shared across subdomains)
      signOutCognito();
      
      // Redirect to login page
      window.location.href = '/';
      
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
      // Force local logout even if everything fails
      // Set logout flag to notify other apps
      localStorage.setItem('sso_logout_flag', 'true');
      setTimeout(() => {
        localStorage.removeItem('sso_logout_flag');
      }, 1000);
      
      // Clear SSO cookie
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isLocalhost) {
        document.cookie = 'sso_token=; domain=.syberkey.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None; Secure';
      }
      setUser(null);
      setToken(null);
      localStorage.clear();
      window.location.href = '/';
    }
  };

  const value = {
    user,
    token,
    login,
    loginWithSyberKey,
    createSession,
    setToken,
    setUser,
    logout,
    loading,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};